/* ==========================================================================
   STYLED - Styled Premium theme layer
   Hero slideshow, seamless marquee, scroll reveal, sticky header.
   Self-contained. Safe to load on every page. Re-inits in the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Announce that this script is running, synchronously, before anything can
     fail below. styled-refine.css keeps .sp-reveal fully visible unless
     html.sp-js is present, so if this file never loads, errors on an older
     browser, or is blocked, every section still renders. Nothing on the page
     is hidden by CSS that only JavaScript can bring back. */
  document.documentElement.classList.add('sp-js');

  /* ----------------------------------------------------------------------
     1. HERO SLIDESHOW
     ---------------------------------------------------------------------- */

  function initHero(root) {
    if (!root || root.dataset.spHeroReady === 'true') return;
    root.dataset.spHeroReady = 'true';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-sp-slide]'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-sp-dot]'));
    var prev = root.querySelector('[data-sp-prev]');
    var next = root.querySelector('[data-sp-next]');

    var index = 0;
    var timer = null;
    var delay = (parseInt(root.dataset.speed, 10) || 6) * 1000;
    var autoplay = root.dataset.autoplay === 'true' && !reduceMotion;

    function show(i) {
      index = (i + slides.length) % slides.length;

      slides.forEach(function (slide, n) {
        slide.classList.toggle('is-active', n === index);
      });

      dots.forEach(function (dot, n) {
        var on = n === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      // Restart video on the slide that just became visible
      var video = slides[index].querySelector('video');
      if (video && typeof video.play === 'function') {
        try {
          video.currentTime = 0;
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } catch (e) {
          /* autoplay blocked - the poster frame still shows */
        }
      }
    }

    function start() {
      if (!autoplay) return;
      stop();
      timer = setInterval(function () {
        show(index + 1);
      }, delay);
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function goto(i) {
      show(i);
      start();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goto(index - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        goto(index + 1);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goto(parseInt(dot.dataset.index, 10) || 0);
      });
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    // Arrow keys when the hero has focus
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goto(index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goto(index + 1);
      }
    });

    // Swipe on touch devices
    var touchX = null;
    root.addEventListener(
      'touchstart',
      function (e) {
        touchX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 45) goto(dx < 0 ? index + 1 : index - 1);
        touchX = null;
      },
      { passive: true }
    );

    // Stop the timer while the tab is hidden
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    show(0);
    start();
  }

  /* ----------------------------------------------------------------------
     2. MARQUEE - clone the group so the -50% loop has no gap
     ---------------------------------------------------------------------- */

  function initMarquee(track) {
    if (!track || track.dataset.spMarqueeReady === 'true') return;

    var group = track.querySelector('[data-sp-marquee-group]');
    if (!group) return;

    track.dataset.spMarqueeReady = 'true';

    // Widen a short group so it always overflows the viewport before cloning
    var guard = 0;
    while (group.offsetWidth < window.innerWidth && guard < 8) {
      group.innerHTML += group.innerHTML;
      guard++;
    }

    var clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-sp-marquee-group');
    track.appendChild(clone);
  }

  /* ----------------------------------------------------------------------
     3. SCROLL REVEAL
     ---------------------------------------------------------------------- */

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    if (!('IntersectionObserver' in window)) return null;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('sp-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    return observer;
  }

  /* Belt and braces for the observer above.

     The observer is the primary mechanism, but when it misses an element that
     element stays at opacity 0 while still occupying its full height, so an
     entire section silently disappears and leaves a tall blank band. That is
     exactly what happened to the category slider on the homepage. Misses are
     easy to cause: a fast scroll, a screenshot tool that resizes the viewport
     instead of scrolling it, a section injected after the observer ran, or a
     zero-height element at observe time.

     This is a second, independent check on a different mechanism - scroll and
     resize plus getBoundingClientRect - so the two do not share a failure
     mode. It reveals anything already in view and unbinds itself once
     everything has been revealed, so it costs nothing after the first pass. */
  var revealBound = false;
  var revealTicking = false;

  function revealInView() {
    var pending = document.querySelectorAll('.sp-reveal:not(.sp-in)');

    if (!pending.length) {
      window.removeEventListener('scroll', onRevealScroll);
      window.removeEventListener('resize', onRevealScroll);
      revealBound = false;
      return;
    }

    var vh = window.innerHeight || document.documentElement.clientHeight;

    Array.prototype.forEach.call(pending, function (el) {
      /* top < viewport height catches anything in view AND anything already
         scrolled past. Testing for still-on-screen as well (r.bottom > 0) was
         the original bug: jump to the foot of the page, or scroll fast enough
         that the intermediate frames never paint, and the observer never sees
         those middle sections intersect. They then sit at opacity 0 forever,
         holding their full height - a tall blank band where a section should
         be. Once an element's top has crossed the bottom of the viewport it
         has been reached, so it gets revealed whether or not it is still
         on screen. */
      if (el.getBoundingClientRect().top < vh) el.classList.add('sp-in');
    });
  }

  function onRevealScroll() {
    if (revealTicking) return;
    revealTicking = true;
    window.requestAnimationFrame(function () {
      revealTicking = false;
      revealInView();
    });
  }

  function bindRevealFallback() {
    if (revealBound) return;
    revealBound = true;
    window.addEventListener('scroll', onRevealScroll, { passive: true });
    window.addEventListener('resize', onRevealScroll);
  }

  function initReveal(scope) {
    var targets = (scope || document).querySelectorAll('.sp-reveal:not(.sp-in)');
    if (!targets.length) return;

    var io = getObserver();

    if (!io || reduceMotion) {
      targets.forEach(function (el) {
        el.classList.add('sp-in');
      });
      return;
    }

    targets.forEach(function (el) {
      io.observe(el);
    });

    bindRevealFallback();
    revealInView();
  }

  /* ----------------------------------------------------------------------
     4. STICKY HEADER SHADOW
     ---------------------------------------------------------------------- */

  function initStickyHeader() {
    if (document.documentElement.dataset.spStickyReady === 'true') return;
    document.documentElement.dataset.spStickyReady = 'true';

    var ticking = false;

    function update() {
      document.body.classList.toggle('sp-header-stuck', window.scrollY > 40);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ----------------------------------------------------------------------
     5. WISHLIST

     Saved in this browser only (localStorage). It needs no app, but it
     does not follow a shopper to another device or sync to an account.
     ---------------------------------------------------------------------- */

  var WISH_KEY = 'sp_wishlist';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISH_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode or storage full - the session still works */
    }
  }

  function inWishlist(list, url) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].url === url) return i;
    }
    return -1;
  }

  var heartSvg =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M12 20.7 4.2 13a4.6 4.6 0 0 1 0-6.5 4.6 4.6 0 0 1 6.5 0l1.3 1.3 1.3-1.3a4.6 4.6 0 0 1 6.5 0 4.6 4.6 0 0 1 0 6.5z" ' +
    'stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

  function updateWishlistCount() {
    var list = readWishlist();
    var link = document.querySelector('.sp-wishlist-link');
    if (!link) return;

    var count = link.querySelector('.sp-wishlist-count');
    if (count) count.textContent = list.length;
    link.classList.toggle('has-items', list.length > 0);
    link.setAttribute('aria-label', 'Wishlist, ' + list.length + ' saved');
  }

  function syncHearts() {
    var list = readWishlist();
    document.querySelectorAll('.sp-card-heart').forEach(function (btn) {
      btn.classList.toggle('is-on', inWishlist(list, btn.dataset.url) > -1);
    });
  }

  function addCardHearts(scope) {
    var cards = (scope || document).querySelectorAll('.card-wrapper:not([data-sp-heart-ready])');

    cards.forEach(function (card) {
      var link = card.querySelector('a.full-unstyled-link[href], .card__heading a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href || href.indexOf('/products/') === -1) return;

      card.dataset.spHeartReady = 'true';

      var img = card.querySelector('.card__media img, .media img');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sp-card-heart';
      btn.innerHTML = heartSvg;
      btn.dataset.url = href;
      btn.dataset.title = (link.textContent || '').trim();
      btn.dataset.img = img ? img.getAttribute('src') || '' : '';
      btn.setAttribute('aria-label', 'Save to wishlist');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var list = readWishlist();
        var at = inWishlist(list, btn.dataset.url);

        if (at > -1) {
          list.splice(at, 1);
        } else {
          list.push({ url: btn.dataset.url, title: btn.dataset.title, img: btn.dataset.img });
        }

        writeWishlist(list);
        syncHearts();
        updateWishlistCount();
        renderWishlistPanel();
      });

      card.appendChild(btn);
    });

    syncHearts();
  }

  function renderWishlistPanel() {
    var list = readWishlist();
    var panel = document.querySelector('.sp-wish-panel');
    if (!panel) return;

    var ul = panel.querySelector('.sp-wish-panel__list');
    if (!ul) return;

    if (!list.length) {
      ul.innerHTML = '<li class="sp-wish-panel__empty">Nothing saved yet. Tap the heart on any product to keep it here.</li>';
      return;
    }

    ul.innerHTML = '';

    list.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'sp-wish-panel__item';

      var a = document.createElement('a');
      a.className = 'sp-wish-panel__name';
      a.href = item.url;

      if (item.img) {
        var im = document.createElement('img');
        im.src = item.img;
        im.alt = '';
        im.loading = 'lazy';
        li.appendChild(im);
      }

      a.textContent = item.title || 'View product';
      li.appendChild(a);

      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'sp-wish-panel__remove';
      rm.innerHTML = '&times;';
      rm.setAttribute('aria-label', 'Remove from wishlist');
      rm.addEventListener('click', function () {
        var current = readWishlist();
        var at = inWishlist(current, item.url);
        if (at > -1) current.splice(at, 1);
        writeWishlist(current);
        syncHearts();
        updateWishlistCount();
        renderWishlistPanel();
      });

      li.appendChild(rm);
      ul.appendChild(li);
    });
  }

  function initWishlist() {
    if (document.documentElement.dataset.spWishReady === 'true') return;

    var icons = document.querySelector('.header__icons');
    if (!icons) return;

    document.documentElement.dataset.spWishReady = 'true';

    // Header button, placed just before the cart
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sp-wishlist-link';
    btn.innerHTML = heartSvg + '<span class="sp-wishlist-count">0</span>';

    var cart = icons.querySelector('#cart-icon-bubble');
    if (cart) {
      icons.insertBefore(btn, cart);
    } else {
      icons.appendChild(btn);
    }

    // Slide-out panel
    var panel = document.createElement('div');
    panel.className = 'sp-wish-panel';
    panel.innerHTML =
      '<div class="sp-wish-panel__scrim" data-sp-wish-close></div>' +
      '<div class="sp-wish-panel__inner" role="dialog" aria-modal="true" aria-label="Wishlist">' +
      '<div class="sp-wish-panel__head">' +
      '<h2 class="sp-wish-panel__title">Wishlist</h2>' +
      '<button type="button" class="sp-wish-panel__close" data-sp-wish-close aria-label="Close">&times;</button>' +
      '</div><ul class="sp-wish-panel__list"></ul></div>';
    document.body.appendChild(panel);

    function open() {
      renderWishlistPanel();
      panel.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      panel.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    btn.addEventListener('click', open);

    panel.querySelectorAll('[data-sp-wish-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    updateWishlistCount();
  }

  /* ----------------------------------------------------------------------
     BOOT
     ---------------------------------------------------------------------- */

  /* ------------------------------------------------------------------
     SCROLL TO TOP
     Sits bottom LEFT on purpose. The chat widget on this store sits
     bottom right, and two floating circles in one corner stack badly.
     Move the `left` to `right` in styled-refine.css to swap sides.
     ------------------------------------------------------------------ */

  function initScrollTop() {
    if (document.documentElement.dataset.spTopReady === 'true') return;
    document.documentElement.dataset.spTopReady = 'true';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sp-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>';
    document.body.appendChild(btn);

    var shown = false;

    function sync() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var should = y > 600;
      if (should === shown) return;
      shown = should;
      btn.classList.toggle('is-visible', shown);
    }

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          sync();
          ticking = false;
        });
      },
      { passive: true }
    );

    btn.addEventListener('click', function () {
      var reduce =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    });

    sync();
  }

  function initAll(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-sp-hero]').forEach(initHero);
    root.querySelectorAll('[data-sp-marquee]').forEach(initMarquee);
    initReveal(root);
    initStickyHeader();
    initWishlist();
    initScrollTop();
    addCardHearts(root);
    updateWishlistCount();

    // ::selection styling hook
    document.body.classList.add('sp-selection-host');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify theme editor: re-init a section after it reloads
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    initAll(e.target);
  });
})();
